import React from "react";
import CardBase from "@/components/cards/CardBase";
import { Badge } from "@/components/ui/badge";
import IconProvider from "@/components/ui/IconProvider";
import { StyledTextDisplay } from "@/components/ui/StyledTextDisplay";
import { buildFolderBreadcrumbs } from "@/lib/stores/folderStore";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { Variable } from "lucide-react";

interface VariablesWidgetProps {
  id?: string;
  gameId?: string;
  title: string;
  elementType: string;
  elementTypeLabel: string;
  elementCount: number;
  description?: string;
  groupName?: string;
  lastUpdate?: string;
  folder?: string;
  itemType?: "variables";
  shadowType?: "black" | "gradient" | "none" | "color" | "status";
  shadowColor?: string;
  onDelete?: (id: string, gameId: string) => void;
  onEdit?: (id: string) => void;
}

const VariablesWidget: React.FC<VariablesWidgetProps> = ({
  id,
  gameId,
  title,
  elementType,
  elementTypeLabel,
  elementCount,
  description,
  groupName,
  lastUpdate,
  folder,
  itemType = "variables",
  shadowType = "black",
  shadowColor = "#000000",
  onDelete,
  onEdit,
}) => {
  const { selectedGame } = useSimplifiedUserData();
  const descriptionContainerRef = React.useRef<HTMLDivElement>(null);
  const [maxLines, setMaxLines] = React.useState<number>(15);

  // Calculate max lines based on container height
  React.useEffect(() => {
    if (!descriptionContainerRef.current) return;

    const updateMaxLines = () => {
      const container = descriptionContainerRef.current;
      if (!container) return;

      const containerHeight = container.clientHeight;
      const lineHeight = 14 * 1.625;
      const calculatedLines = Math.floor(containerHeight / lineHeight);
      
      setMaxLines(Math.max(1, calculatedLines));
    };

    updateMaxLines();

    const resizeObserver = new ResizeObserver(updateMaxLines);
    resizeObserver.observe(descriptionContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [description]);

  // Generate breadcrumbs for the item's folder
  const breadcrumbs = React.useMemo(() => {
    if (!selectedGame?.id || !folder) return [];
    return buildFolderBreadcrumbs(
      selectedGame.id,
      itemType,
      folder,
      null
    );
  }, [selectedGame?.id, folder, itemType]);

  // Render breadcrumbs with truncation
  const renderBreadcrumbs = () => {
    if (breadcrumbs.length === 0) return null;
    const maxBreadcrumbs = 3;
    const shouldTruncate = breadcrumbs.length > maxBreadcrumbs;

    if (shouldTruncate) {
      const firstPart = breadcrumbs.slice(0, 1);
      const lastPart = breadcrumbs.slice(-2);
      const displayBreadcrumbs = [...firstPart, "...", ...lastPart];

      return (
        <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
          <span className="truncate">{displayBreadcrumbs.join(" / ")}</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
          <span className="truncate">{breadcrumbs.join(" / ")}</span>
        </div>
      );
    }
  };

  // Get badge color based on element type
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'number':
        return 'bg-blue-100 text-blue-700';
      case 'percentage':
        return 'bg-green-100 text-green-700';
      case 'range':
        return 'bg-purple-100 text-purple-700';
      case 'type':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const elementCountText = `${elementCount} ${elementCount === 1 ? 'value' : 'values'}`;

  return (
    <CardBase
      className="flex flex-col h-full w-full flex-1"
      shadowType={shadowType}
      shadowColor={shadowColor}
    >
      <div className="flex items-center gap-3 p-4 pb-2 border-b border-gray-300 shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Variable className="h-5 w-5 text-primary shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <div className="text-lg font-semibold text-foreground leading-tight truncate">
              {title}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Badge className={`text-xs px-2 py-0.5 ${getTypeBadgeColor(elementType)}`}>
                {elementTypeLabel}
              </Badge>
              <span className="text-muted-foreground">{elementCountText}</span>
              {groupName && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-muted-foreground truncate">Group: {groupName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div 
          ref={descriptionContainerRef}
          className="px-4 pt-2 pb-2 text-foreground flex-1 min-h-0 overflow-hidden flex items-start"
        >
          <StyledTextDisplay 
            content={description} 
            maxLines={maxLines}
            className="text-sm w-full"
          />
        </div>
      )}

      {/* Show element count if no description */}
      {!description && (
        <div className="px-4 pt-2 pb-2 text-muted-foreground text-sm flex-1">
          {elementCountText}
        </div>
      )}

      {/* Last update */}
      {lastUpdate && (
        <div className="px-4 pb-2 mt-auto text-xs text-gray-600 shrink-0">
          Last update: {lastUpdate}
        </div>
      )}

      {/* Action buttons */}
      {(onEdit || onDelete) && (
        <div className="flex justify-between items-center p-4 pb-2 border-t border-gray-300 shrink-0">
          <div className="flex justify-between items-center min-w-0 flex-1">
            {/* Action buttons on the left */}
            <div className="flex gap-3">
              {onEdit && id && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(id);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconProvider
                    iconstring="edit"
                    size={14}
                    color="#000000"
                    hoverColor="#E0E0E0"
                  />
                </div>
              )}
              {onDelete && id && gameId && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(id, gameId);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconProvider
                    iconstring="trash"
                    size={14}
                    color="#000000"
                    hoverColor="#FA8072"
                  />
                </div>
              )}
            </div>

            {/* Breadcrumbs on the right */}
            {renderBreadcrumbs()}
          </div>
        </div>
      )}
    </CardBase>
  );
};

export default VariablesWidget;
